import { FormEvent, useEffect, useState } from "react"

import { TextField } from "@mui/material"
import Dialog from "components/common/dialog/dialog"
import { useTranslation } from "react-i18next"
import { DeleteTransactionsTarget } from "ts/types/Transaction"

// Equals the backend's threshold for running a delete as a background task.
export const DELETE_TRANSACTIONS_CONFIRM_THRESHOLD = 50

interface IProps {
  open: boolean
  target: DeleteTransactionsTarget | null
  isPending: boolean
  onClose: () => void
  onConfirm: () => void
}

const DeleteTransactionsDialog = ({
  open,
  target,
  isPending,
  onClose,
  onConfirm,
}: IProps) => {
  const { t } = useTranslation()
  const [confirmText, setConfirmText] = useState("")
  const confirmWord = t("allTransactions.deleteConfirmWord")

  useEffect(() => {
    if (open) setConfirmText("")
  }, [open])

  const isTypedConfirmRequired =
    target?.kind === "batch" &&
    target.count >= DELETE_TRANSACTIONS_CONFIRM_THRESHOLD
  const isConfirmed =
    !isTypedConfirmRequired ||
    confirmText.trim().toLowerCase() === confirmWord.toLowerCase()

  const titleText = !target
    ? undefined
    : target.kind === "single"
      ? target.title
        ? t("allTransactions.deleteTransactionTitle", {
            title: target.title,
            interpolation: { escapeValue: false },
          })
        : t("allTransactions.deleteThisTransaction")
      : t("allTransactions.deleteTransactions", { count: target.count })

  const descriptionText = [
    t("allTransactions.deleteCantBeUndone"),
    target?.kind === "batch" && target.scope === "allMatching"
      ? t("allTransactions.deleteIncludesAllMatching")
      : null,
    target?.kind === "batch" && target.scope === "allMatchingExcept"
      ? t("allTransactions.deleteIncludesAllMatchingExceptUnticked")
      : null,
  ]
    .filter(Boolean)
    .join(" ")

  const handleConfirm = () => {
    if (isConfirmed && !isPending) onConfirm()
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    handleConfirm()
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isPending) onClose()
      }}
      titleText={titleText}
      descriptionText={descriptionText}
      actionAcceptText={t("delete")}
      isLoading={isPending}
      actionAcceptButtonProps={{ disabled: isPending || !isConfirmed }}
      onActionButtonClick={handleConfirm}
    >
      {isTypedConfirmRequired && (
        <form onSubmit={handleSubmit}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={t("allTransactions.typeDeleteToConfirm", {
              word: confirmWord,
            })}
            value={confirmText}
            disabled={isPending}
            onChange={(event) => setConfirmText(event.target.value)}
          />
        </form>
      )}
    </Dialog>
  )
}

export default DeleteTransactionsDialog
